import React, { useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";
import { exportToCSV } from "../../../utils/exportCsv";
import { MdLocationOn, MdOutlineLinearScale, MdOutlinePalette } from "react-icons/md";
import {
  FiEdit2,
  FiSearch,
  FiMail,
  FiPhone,
  FiCalendar,
  FiUsers,
  FiSettings,
  FiX,
  FiCheckCircle,
} from "react-icons/fi";

// Deterministic vibrant avatar background based on tenant id
const getAvatarGradient = (id = 0) => {
  const gradients = [
    "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
    "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
    "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  ];
  return gradients[Math.abs(Number(id) || 0) % gradients.length];
};

const customTableStyles = {
  table: {
    style: {
      backgroundColor: "#ffffff",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      borderTop: "1px solid #edf2f7",
      borderBottom: "1px solid #e2e8f0",
      minHeight: "48px",
    },
  },
  headCells: {
    style: {
      fontSize: "12px",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      color: "#475569",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      minHeight: "68px",
      fontSize: "13px",
      color: "#1e293b",
      borderBottom: "1px solid #f1f5f9",
      "&:hover": {
        backgroundColor: "#f8fafc",
        transition: "all 0.15s ease",
      },
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
};

const SupplyBeamLocations = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("access_token");

  const [search, setSearch] = useState("");

  const {
    data: vendorsData,
    isLoading: vendorsLoading,
    error: vendorsError,
  } = useFetchVendorsQuery(token);

  useMemo(() => {
    if (vendorsError) {
      toast.error("Failed to load clients", { id: "vendorsErr" });
    }
  }, [vendorsError]);

  const allClients = vendorsData?.body || [];

  const filtered = allClients.filter((c) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (c.name && c.name.toLowerCase().includes(q)) ||
      String(c.tenant_id).includes(q) ||
      (c.slug && c.slug.toLowerCase().includes(q)) ||
      (c.email && c.email.toLowerCase().includes(q))
    );
  });

  const columns = [
    {
      name: "S.No",
      selector: (row, idx) => idx + 1,
      width: "75px",
      cell: (row, idx) => (
        <span
          style={{
            fontSize: "12px",
            fontWeight: 600,
            color: "#64748b",
            background: "#f1f5f9",
            borderRadius: "6px",
            padding: "2px 8px",
          }}
        >
          #{idx + 1}
        </span>
      ),
    },
    {
      name: "Client Name & Tenant",
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
      cell: (row) => {
        const initials = (row.name || "C")
          .split(" ")
          .map((n) => n[0])
          .slice(0, 2)
          .join("")
          .toUpperCase();

        return (
          <div className="d-flex align-items-center gap-3 py-2">
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                background: getAvatarGradient(row.tenant_id),
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "700",
                fontSize: "14px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <div>
              <div
                style={{
                  fontWeight: 600,
                  color: "#0f172a",
                  fontSize: "14px",
                  lineHeight: "1.3",
                }}
              >
                {row.name}
              </div>
              <div className="d-flex align-items-center gap-2 mt-1">
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    padding: "1px 6px",
                    borderRadius: "4px",
                    background: "#f1f5f9",
                    color: "#475569",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  ID: #{row.tenant_id}
                </span>
                {row.slug && (
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#64748b",
                      fontFamily: "monospace",
                    }}
                  >
                    {row.slug}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      name: "Email Address",
      selector: (row) => row.email || "—",
      sortable: true,
      grow: 1.5,
      cell: (row) =>
        row.email ? (
          <div
            className="d-flex align-items-center gap-2 text-truncate"
            style={{ fontSize: "13px", color: "#334155" }}
            title={row.email}
          >
            <FiMail size={13} className="text-muted flex-shrink-0" />
            <span className="text-truncate">{row.email}</span>
          </div>
        ) : (
          <span className="text-muted" style={{ fontSize: "13px" }}>
            —
          </span>
        ),
    },
    {
      name: "Contact Number",
      selector: (row) => row.mobile || "—",
      width: "150px",
      cell: (row) =>
        row.mobile ? (
          <div
            className="d-flex align-items-center gap-2"
            style={{ fontSize: "13px", color: "#334155" }}
          >
            <FiPhone size={13} className="text-muted flex-shrink-0" />
            <span>{row.mobile}</span>
          </div>
        ) : (
          <span className="text-muted" style={{ fontSize: "13px" }}>
            —
          </span>
        ),
    },
    {
      name: "Registered On",
      selector: (row) => row.created_at || "",
      sortable: true,
      width: "150px",
      cell: (row) => (
        <div
          className="d-flex align-items-center gap-2 text-muted"
          style={{ fontSize: "12px" }}
        >
          <FiCalendar size={13} className="flex-shrink-0" />
          <span>
            {row.created_at
              ? new Date(row.created_at).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </span>
        </div>
      ),
    },
    {
      name: "Supply Chain Actions",
      width: "220px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-sm d-flex align-items-center gap-1 text-white shadow-xs"
            style={{
              whiteSpace: "nowrap",
              fontSize: "12px",
              fontWeight: 600,
              padding: "6px 12px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
              border: "none",
              boxShadow: "0 2px 4px rgba(79, 70, 229, 0.25)",
              cursor: "pointer",
            }}
            onClick={() =>
              navigate("manage", {
                state: {
                  tenant_id: row.tenant_id,
                  slug: row.slug,
                  name: row.name,
                },
              })
            }
            title="Configure stages, linear order, and checkout transitions"
          >
            <FiSettings size={13} />
            Configure Pipeline
          </button>

          <button
            className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
            style={{
              width: "32px",
              height: "32px",
              padding: "0",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
            }}
            onClick={() =>
              navigate("instances", {
                state: {
                  tenant_id: row.tenant_id,
                  slug: row.slug,
                  name: row.name,
                },
              })
            }
            title="View physical location instances for this tenant"
          >
            <MdLocationOn size={16} className="text-primary" />
          </button>

          <button
            className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
            style={{
              width: "32px",
              height: "32px",
              padding: "0",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
            }}
            onClick={() =>
              navigate("manage", {
                state: {
                  tenant_id: row.tenant_id,
                  slug: row.slug,
                  name: row.name,
                  initialTab: "theme",
                },
              })
            }
            title="Configure Theme & Appearance for this client"
          >
            <MdOutlinePalette size={16} style={{ color: "#7c3aed" }} />
          </button>
        </div>
      ),
      ignoreRowClick: true,
    },
  ];

  const handleExportCSV = () => {
    if (!filtered || filtered.length === 0) {
      toast.error("No clients available to export");
      return;
    }
    const exportColumns = [
      { name: "Tenant ID", selector: (r) => r.tenant_id },
      { name: "Client Name", selector: (r) => r.name || "-" },
      { name: "Slug", selector: (r) => r.slug || "-" },
      { name: "Email", selector: (r) => r.email || "-" },
      { name: "Mobile", selector: (r) => r.mobile || "-" },
      { name: "Created At", selector: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "-" },
    ];
    exportToCSV(filtered, exportColumns, "supply_beam_clients.csv");
    toast.success("CSV Downloaded Successfully!");
  };

  return (
    <div
      className="container-fluid p-4"
      style={{
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Hero Header Card */}
      <div
        className="card border-0 shadow-sm p-4 mb-4"
        style={{
          borderRadius: "16px",
          background: "#ffffff",
          border: "1px solid #eef2f6",
        }}
      >
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: "linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                boxShadow: "0 4px 10px rgba(79, 70, 229, 0.3)",
              }}
            >
              <MdOutlineLinearScale size={26} />
            </div>
            <div>
              <h4 className="mb-1 fw-bold text-dark d-flex align-items-center gap-2">
                Supply Beam — Tenant Pipeline & Location Config
              </h4>
              <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                Select a client below to configure their supply chain blueprint, linear flow order, and allowed transit rules.
              </p>
            </div>
          </div>

          {/* Quick Counter & Export */}
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-success btn-sm font-semibold shadow-xs"
              onClick={handleExportCSV}
            >
              Export CSV
            </button>
            <div
              className="p-2 px-3 rounded-3 d-flex align-items-center gap-2"
              style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
            >
              <FiUsers size={16} className="text-primary" />
              <div style={{ fontSize: "13px" }}>
                <span className="text-muted">Total Tenants: </span>
                <strong className="text-dark">{allClients.length}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div
        className="card border-0 shadow-sm"
        style={{
          borderRadius: "16px",
          background: "#ffffff",
          border: "1px solid #eef2f6",
          overflow: "hidden",
        }}
      >
        {/* Table Toolbar */}
        <div className="p-4 border-bottom bg-white d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              Registered Clients Directory
              <span
                className="badge bg-light text-primary border"
                style={{ fontSize: "12px", fontWeight: 600 }}
              >
                {filtered.length} {filtered.length === 1 ? "Client" : "Clients"}
              </span>
            </h5>
            <div className="text-muted" style={{ fontSize: "13px" }}>
              Click "Configure Pipeline" on any client row to manage their stages and transition rules.
            </div>
          </div>

          {/* Search Box */}
          <div className="input-group" style={{ maxWidth: "340px" }}>
            <span
              className="input-group-text bg-light text-muted border-end-0"
              style={{ borderRadius: "8px 0 0 8px" }}
            >
              <FiSearch size={14} />
            </span>
            <input
              type="text"
              className="form-control form-control-sm border-start-0"
              placeholder="Search by client name, ID, slug, or email..."
              style={{ height: "40px", fontSize: "13px" }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="btn btn-sm btn-light border-start-0 border"
                style={{ borderRadius: "0 8px 8px 0", color: "#64748b" }}
                onClick={() => setSearch("")}
                title="Clear search"
              >
                <FiX size={14} />
              </button>
            )}
          </div>
        </div>

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={filtered}
          progressPending={vendorsLoading}
          progressComponent={
            <div className="p-5 text-center text-muted">
              <div className="spinner-border text-primary mb-2" role="status" />
              <div>Loading client tenants...</div>
            </div>
          }
          pagination
          paginationPerPage={15}
          paginationRowsPerPageOptions={[10, 15, 25, 50]}
          highlightOnHover
          responsive
          customStyles={customTableStyles}
          noDataComponent={
            <div className="p-5 text-center text-muted">
              <FiUsers size={40} className="text-muted opacity-40 mb-2" />
              <div className="fw-semibold">No clients match your search query.</div>
              <p className="small text-muted mt-1">
                Try searching with a different name or tenant ID.
              </p>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default SupplyBeamLocations;
